import { Injectable } from '@angular/core';
import { NfseData } from './nfse-parser';
import { ItemTexto, interpretarItens, montarColunas, montarFluxo } from './pdf-texto';

/**
 * PDF sem camada de texto - nota digitalizada ou fotografada. Nao e erro de
 * leitura: nao ha o que extrair sem OCR. Quem chama trata como aviso ao
 * usuario, em vez de devolver um formulario vazio que parece falha nossa.
 * No acervo de PDFs entregues pelo portal, 10 de 61 sao assim.
 */
export class PdfSemTextoError extends Error {
  constructor() {
    super('PDF sem texto: documento digitalizado (imagem).');
    this.name = 'PdfSemTextoError';
  }
}

// Servico orquestrador: recebe um File (PDF do tipo DAN/DANFE), extrai o
// texto via pdf.js e devolve um NfseData populado.
//
// pdf.js eh lazy-loaded - so baixa quando o usuario realmente anexa um PDF.
// Isso mantem o bundle inicial leve (~200KB economizados ate o primeiro
// uso). O worker tambem eh lazy via URL relativa ao import.meta.url, que
// o esbuild do @angular/build resolve em build-time.

@Injectable({ providedIn: 'root' })
export class PdfParserService {
  private pdfLibPromise: Promise<typeof import('pdfjs-dist')> | null = null;
  private workerDisabled = false;

  /**
   * Le um PDF e devolve os campos extraidos da NFSe.
   * Throws se o PDF nao puder ser aberto (corrompido, criptografado, etc.).
   * Retorna NfseData com campos vazios/zero quando o parser nao reconhece
   * o layout - quem chama decide se aplica no form ou avisa o usuario.
   *
   * Estrategia: tenta primeiro com web worker (mais rapido); se o worker
   * falhar ao inicializar (404 do worker file, MIME type errado no server,
   * bloqueio de CSP), faz fallback automatico pra fake worker (mais lento,
   * roda no main thread, mas funciona em qualquer ambiente).
   */
  async parseDanfe(file: File): Promise<NfseData> {
    const buf = await file.arrayBuffer();
    let paginas: ItemTexto[][];
    try {
      paginas = await this.extrairItens(buf);
    } catch (e) {
      // Worker falhou (404 do worker file, MIME errado, CSP). Limpa o
      // workerSrc pra cair em fake worker (mais lento mas funciona) e
      // tenta de novo. Marca a flag pra nao retentar com worker depois.
      console.warn('[pdf-parser] worker falhou, retrying sem worker:', e);
      const pdfjs = await this.loadPdfJs();
      pdfjs.GlobalWorkerOptions.workerSrc = '';
      this.workerDisabled = true;
      paginas = await this.extrairItens(buf);
    }

    // Nada de texto = PDF de imagem. Avisar e melhor do que devolver um
    // formulario vazio, que o usuario le como falha da leitura.
    const data = interpretarItens(paginas, file.name);
    if (!data) {
      throw new PdfSemTextoError();
    }
    return data;
  }

  /**
   * Texto do PDF no modo FLUXO - a extracao historica, mantida como era:
   * junta os itens na ordem em que o pdf.js os entrega e quebra a linha
   * quando o y muda. E o que os parsers municipais e o do CTe esperam.
   * Publica por ser util em debug e teste.
   */
  async extractText(buf: ArrayBuffer): Promise<string> {
    return montarFluxo(await this.extrairItens(buf));
  }

  /**
   * Texto do PDF no modo COLUNAS - itens ordenados por linha (y) e, dentro
   * da linha, por posicao horizontal (x), com 3+ espacos marcando a troca de
   * coluna. E o que o DANFSe v2.0 exige para casar rotulo e valor; ver o
   * cabecalho do bloco DANFSe v2.0 no nfse-parser.
   */
  async extractTextColunas(buf: ArrayBuffer): Promise<string> {
    return montarColunas(await this.extrairItens(buf));
  }

  /**
   * Le o PDF uma unica vez e devolve os itens de texto com posicao, por
   * pagina. Os dois modos de texto saem daqui sem reabrir o documento.
   */
  private async extrairItens(buf: ArrayBuffer): Promise<ItemTexto[][]> {
    const pdfjs = await this.loadPdfJs();
    const loadingTask = pdfjs.getDocument({ data: buf });
    const pdf = await loadingTask.promise;

    const paginas: ItemTexto[][] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const itens: ItemTexto[] = [];
      for (const item of content.items as Array<{ str: string; width?: number; transform: number[] }>) {
        itens.push({
          str: item.str ?? '',
          x: item.transform?.[4] ?? 0,
          y: item.transform?.[5] ?? 0,
          // transform[3] = escala vertical da fonte; serve de altura da linha
          alt: Math.abs(item.transform?.[3] ?? 0) || 8,
          larg: item.width ?? 0
        });
      }
      paginas.push(itens);
    }

    return paginas;
  }

  private async loadPdfJs(): Promise<typeof import('pdfjs-dist')> {
    if (this.pdfLibPromise) return this.pdfLibPromise;

    this.pdfLibPromise = (async () => {
      const lib = await import('pdfjs-dist');
      // Worker: arquivo em public/ (servido na raiz). Usamos extensao .js
      // em vez de .mjs porque alguns servidores (IIS, Apache default,
      // alguns nginx) servem .mjs com MIME application/octet-stream, e
      // o browser recusa carregar script de modulo com MIME errado.
      // .js eh universalmente servido como text/javascript.
      if (this.workerDisabled) {
        lib.GlobalWorkerOptions.workerSrc = '';
      } else {
        const baseHref = document.querySelector('base')?.getAttribute('href') ?? '/';
        lib.GlobalWorkerOptions.workerSrc = baseHref.replace(/\/?$/, '/') + 'pdf.worker.min.js';
      }
      return lib;
    })();

    return this.pdfLibPromise;
  }
}

